import type { IntlShape } from 'react-intl'
import { getCheckInCheckOutText } from 'components/Search/utils'
import { DMY_CASUAL_SHORT_FORMAT, INTL_DAY_MONTH_NAME, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isItemCancelled } from 'lib/order/orderUtils'
import moment from 'moment'
import { deriveFlightDisplayData, formatFlightRouteLabel, formatFlightTypeLabel } from './flightDisplayUtils'

export interface HotelOrderTileData {
  bookingType: 'hotel'
  itemId: string
  orderId: string
  offerId: string
  propertyName: string
  checkInDate?: string
  imageId?: string
  checkOutDate?: string
  occupancy?: App.Occupants
}

export interface ExperienceOrderTileData {
  bookingType: 'experience'
  itemId: string
  orderId: string
  offerId: string
  propertyName: string
  checkInDate?: string
  checkOutDate?: string
  imageId?: string
  occupancy?: App.Occupants
  time?: string
}

export interface FlightOrderTileData {
  bookingType: 'flight'
  itemId: string
  orderId: string
  offerId: string
  reservationId?: string
}

export type OrderTileData = HotelOrderTileData | ExperienceOrderTileData | FlightOrderTileData

export function getFlightBookingItemIds(tiles: Array<OrderTileData>): Array<string> {
  return tiles.flatMap(tile => tile.bookingType === 'flight' ? [tile.itemId] : [])
}

export function getFlightDetailsStateByItemIds(
  flightDetailsState: App.State['orders']['flightDetails'],
  itemIds: Array<string>,
): App.State['orders']['flightDetails'] {
  const relevantFlightDetailsState: App.State['orders']['flightDetails'] = {}

  itemIds.forEach(itemId => {
    if (flightDetailsState[itemId]) {
      relevantFlightDetailsState[itemId] = flightDetailsState[itemId]
    }
  })

  return relevantFlightDetailsState
}

export function getFlightBookingTag(
  intl: IntlShape,
  flightDetails?: App.FlightOrderItemDetails,
): string {
  const {
    departureDate,
    arrivalDate,
    departureLabel,
    arrivalLabel,
    isReturnFlight,
    isDirectFlight,
  } = deriveFlightDisplayData(flightDetails)

  const routeText = formatFlightRouteLabel(intl, departureLabel, arrivalLabel)
  const flightTypeText = formatFlightTypeLabel(intl, isReturnFlight, isDirectFlight)

  let dateText = ''
  if (departureDate) {
    if (isReturnFlight && arrivalDate) {
      dateText = getCheckInCheckOutText(departureDate, arrivalDate)
    } else {
      dateText = moment(departureDate).format(DMY_CASUAL_SHORT_FORMAT)
    }
  }

  const flightTagParts = [routeText, flightTypeText]
  if (dateText) {
    flightTagParts.push(dateText)
  }

  return flightTagParts.join(' · ')
}

export function buildBookingTag({
  intl,
  tile,
  flightDetailsState,
}: {
  intl: IntlShape
  tile: OrderTileData
  flightDetailsState: App.State['orders']['flightDetails']
}): string {
  if (tile.bookingType === 'flight') {
    return getFlightBookingTag(intl, flightDetailsState[tile.itemId]?.flight)
  }

  const parts: Array<string> = [tile.propertyName]

  if (tile.checkInDate && tile.checkOutDate) {
    parts.push(getCheckInCheckOutText(tile.checkInDate, tile.checkOutDate))
  } else if (tile.checkInDate) {
    const formattedBookingDate = tile.bookingType === 'experience' ?
        intl.formatDate(tile.checkInDate, INTL_DAY_MONTH_NAME) :
        moment(tile.checkInDate).format(DMY_CASUAL_SHORT_FORMAT)
    parts.push(formattedBookingDate)
  }

  if (tile.bookingType === 'experience' && tile.time) {
    parts.push(tile.time)
  }

  if (tile.occupancy?.adults) {
    parts.push(intl.formatMessage(
      {
        id: 'hd1_9T_9cOUVixrwCqmW',
        defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
        description: 'Pluralised adult count label for upcoming booking tile on chat page',
      },
      { count: tile.occupancy.adults },
    ))
  }

  return parts.join(' · ')
}

export function getOrderTileDataList(order: App.Order): Array<OrderTileData> {
  const tiles: Array<OrderTileData> = []

  for (const item of order.items) {
    if (isItemCancelled(item) || !item.reservation?.startDate || !item.offer?.property?.name) continue
    tiles.push({
      bookingType: 'hotel',
      itemId: item.id,
      orderId: order.id,
      offerId: item.offerId,
      propertyName: item.offer.property.name,
      checkInDate: item.reservation.startDate,
      imageId: item.offer.property?.roomType?.images?.[0]?.id,
      checkOutDate: item.reservation.endDate,
      occupancy: { adults: item.reservation.adults, children: item.reservation.children, infants: item.reservation.infants },
    })
  }

  for (const item of order.bedbankItems) {
    if (isItemCancelled(item) || !item.offer?.property?.name) continue
    tiles.push({
      bookingType: 'hotel',
      itemId: item.id,
      orderId: order.id,
      offerId: item.offer.id,
      propertyName: item.offer.property.name,
      checkInDate: item.checkIn.format(ISO_DATE_FORMAT),
      imageId: item.offer.image?.id,
      checkOutDate: item.checkOut.format(ISO_DATE_FORMAT),
      occupancy: item.rooms[0]?.occupancy,
    })
  }

  for (const item of order.experienceItems) {
    if (isItemCancelled(item) || !item.title) continue
    tiles.push({
      bookingType: 'experience',
      itemId: item.id,
      orderId: order.id,
      offerId: item.experienceId,
      propertyName: item.title,
      checkInDate: item.date,
      time: item.time,
    })
  }

  if (order.hasFlight) {
    for (const item of order.flightItems) {
      if (isItemCancelled(item)) continue
      tiles.push({
        bookingType: 'flight',
        itemId: item.id,
        orderId: order.id,
        offerId: item.reservationId ?? item.id,
        reservationId: item.reservationId,
      })
    }
  }

  return tiles
}
